import { CoachSystemData } from './coachSystemData'
import { DarioSystemData } from './darioSystemData'
import { NotesForPatientState } from './noteForPatient'
import { PatientAccountDataState } from './patientAccountData'
import { PatientAccountOrdersState } from './patientAccountOrders'
import { PatientQuestionnaireState } from './questionnaire'
import { ChecklistState } from './checklist'
import { PatientTasksState } from './patientTasks'
import { PatientDataRequest } from './searchPatients'
import { ClinicalState } from './clinical'
import { SystemEventsLogbookState } from './systemEvent'
import { ReportsState } from '../store/reducers/patientOverview/reports/reducers'
import { TimeLineListState } from './timeline'
import { ChartsState } from '../store/reducers/patientOverview/chart/reducer'
import { Person } from './person'
import {
	DIABETES_TYPES,
	ENGAGE_STATUS,
	LAST_ACTIVITIES_TIME_RANGES,
	PARTS_OF_DAY,
	PMPM_STATUSES,
} from './common'
import { MembershipOrder } from './membership'

/**
 *
 */

export interface MyPatientsListItemBase extends Omit<Person, '_id'> {
	id: string
	phoneNumber: string
	plan: string
	dateStarted: number
	coach: string
	membershipStatus: string
	organization: string
	account: string
	subAccount: string
	rate: number | null
	edit?: boolean
}

export interface MyPatientsListItemExtends {
	gender: string
	ageGroup: string | null
	state: string | null
	country: string | null
	availability: string | null
	languages: string
	diabetesType: string | null
	mskType: string | null
	billableAchieved: boolean
	rpmBillableAchieved: boolean
	comorbidities: string | null
	lastContact: LAST_ACTIVITIES_TIME_RANGES | string | null
	lastContactType: string
}

export type MyPatientsListItem = MyPatientsListItemBase &
	MyPatientsListItemExtends & {
		firstName: string
		lastName: string
		enroll?: PMPM_STATUSES
		products: Array<MembershipOrder>
	}

export type MyPatientsListInnerMultiFilters = Record<
	keyof Pick<
		MyPatientsListItemBase,
		'plan' | 'coach' | 'membershipStatus' | 'organization' | 'account'
	>,
	Array<string>
>

export type MyPatientsListInnerSingleFilters = Record<
	keyof Pick<MyPatientsListItemBase, 'dateStarted'>,
	Array<number> | null
>

export type MyPatientsListExtendedSingleFilters = Pick<
	MyPatientsListItemExtends,
	'billableAchieved' | 'rpmBillableAchieved' | 'country'
>

export type MyPatientsListExtendedMultiFilters = Record<
	keyof Omit<
		MyPatientsListItemExtends,
		| 'billableAchieved'
		| 'rpmBillableAchieved'
		| 'country'
		| 'lastContactType'
	>,
	Array<string>
>

export type MyPatientsMultiFilters = MyPatientsListInnerMultiFilters &
	MyPatientsListExtendedMultiFilters

export type MyPatientsSingleFilters = MyPatientsListInnerSingleFilters &
	MyPatientsListExtendedSingleFilters

export type MyPatientsFilters = MyPatientsMultiFilters & MyPatientsSingleFilters

export type MyPatientsListFilters = {
	[key in keyof MyPatientsFilters]: MyPatientsFilters[key]
}

export type MyPatientsTableModel = MyPatientsListItemBase

export interface PatientResponseFromApi {
	_id: string
	firstName: string | null
	lastName: string | null
	email: string
	phone: string | null
	organizationID: string
	organizationIDCS: string
	account?: string | null
	subAccount?: string | null
	ID: string
	sfContactId?: string
	sfPlan?: string
	engagementRate?: number
	tier1: Partial<Pick<Person, '_id' | 'name'>>
	tier2: Partial<Pick<Person, '_id' | 'name'>>
	tier3: Partial<Pick<Person, '_id' | 'name'>>
	startOfService: string
	status: ENGAGE_STATUS
	imageUrl: string | null
	pmpm?: PMPM_STATUSES
	lastActivity: {
		data: {
			details: {
				['end_time']: number
				typeName: string
			}
		}
	}
	orders?: Array<MembershipOrder>
}

export interface PatientsGetRequestBody {
	text?: string
	name?: string
	email?: string
	phone?: string
	startOfService?: string
	coachName?: string
	account?: string
	organization?: string
	accountName?: string
	coachId?: string
	status?: ENGAGE_STATUS
	pmpmStatus?: string
	gender?: Array<'m' | 'f'>
	state?: Array<string>
	language?: Array<string>
	location?: string
	preferredTime?: Array<PARTS_OF_DAY>
	plan?: string
	diabetesType?: Array<DIABETES_TYPES>
	ageGroup?: Array<{
		minAge: number
		maxAge: number
	}>
	lastActivityFrom?: number
	limit: number
	page: number
}

export interface RecentlyViewedPatient extends Person {
	phone: string
	account: string
	gender: string
}

/**
 *
 */

export interface PatientData {
	_id: string
	email: string
	ID: string
	firstName?: string
	lastName?: string
	imageUrl?: string
	account?: string | null
}

export interface PatientDataTableModel {
	_id: string
	email: string
	ID: string
	firstName: string
	lastName: string
	imageUrl?: string
	plan?: string
	contact?: string
	account?: string | null
	phone?: string
	tier1?: Object | null
	tier2?: Object | null
	tier3?: Object | null
	startOfService?: string
	dateStarted?: string
	status?: string
	organizationIDCS?: string
	// eslint-disable-next-line camelcase
	sub_account?: string
	rate?: string
	pmpm?: string
}
export interface HeadCell {
	disablePadding: boolean
	id: keyof PatientDataTableModel
	label: string
	numeric: boolean
}

export interface AssignPatientDataTableModel {
	_id: string
	email: string
	firstName: string
	lastName: string
	imageUrl?: string
	dateStarted: string
	language: string
	account: string
}

export interface PatientDashboardData {
	coachSystemData: CoachSystemData
	darioSystemData: DarioSystemData
}

export interface NewPatientDashboardData {
	coachSystemData: CoachSystemData
}

export interface PatientCarePlan {
	id: number
	name: string
	ordinal: null | number
	description: null | string
	code: null | string
	['is_current']: boolean
	['is_done']: boolean
	blocks: Array<PatientCarePlanBlock>
	factors: Partial<PatientCarePlanFactors>
}

export interface PatientCarePlanBlock extends Omit<PatientCarePlan, 'blocks'> {
	playlists: Array<PatientCarePlanPlaylist>
}

export interface PatientCarePlanPlaylist
	extends Omit<PatientCarePlan, 'blocks'> {
	items: Array<PatientCarePlanPlaylistItem>
}

export interface PatientCarePlanPlaylistItem
	extends Omit<PatientCarePlan, 'blocks'> {
	type: string
}

export interface PatientScreeningTags {
	attributes: Array<string>
	['risk_level']: string
}

export interface PatientMSKQuestionnnaire {
	['questionnaire_id']: number
	['questionnaire_title']: string
	completed: boolean
	questions: Array<PatientMSKQuestionnaireQuestion>
}

export interface PatientMSKQuestionnaireQuestion {
	['question_id']: number
	['question_title']: 'Where do you feel your pain the most?'
	['answer_id']: [1]
	['response_value']: null
	['answer_value']: ['Neck']
}

export interface PatientCarePlanFactors {
	[key: string]: string
	['intensity']: string
	['joint_area']: string
	['dt_start']: string
	['presentation']: string
}

export interface PatientCarePlanFactorsData {
	['intensity_levels']: Array<{ id: number; title: string }>
	['joint_areas']: PatientCarePlanJointAreas
}

export interface PatientCarePlanJointAreas {
	[key: string]: PatientCarePlanJointAreaData
}

export interface PatientCarePlanJointAreaData {
	id: number
	presentations: Array<{
		id: number
		title: string
	}>
}

export interface PatientFilter {
	key: string
	value: string
}

export interface AppliedFilter {
	filter: PatientFilter
	params: PatientFilter[]
}

export const FilterPatientsList: Array<PatientFilter> = [
	{
		key: 'gender',
		value: 'Birth gender',
	},
	{
		key: 'age',
		value: 'Age group',
	},
	{
		key: 'state',
		value: 'State',
	},
	{
		key: 'languageRegion',
		value: 'Language',
	},
	{
		key: 'contacted',
		value: 'Last time contacted',
	},
	{
		key: 'location',
		value: 'Location',
	},
	{
		key: 'preferred_contact_time',
		value: 'Preferred contact time',
	},
	{
		key: 'userPlan',
		value: 'User plan',
	},
	{
		key: 'diabetesType',
		value: 'Diabetes type',
	},
	{
		key: 'comorbiditiesOpts',
		value: 'Comorbidity',
	},
	{
		key: 'country',
		value: 'Country',
	},
	{
		key: 'mskType',
		value: 'MSK Type',
	},
	{
		key: 'darioBillableAchieved',
		value: 'Dario billable achieved',
	},
]

export enum SEARCH_PATIENTS_ACTION_TYPES {
	GET_ALL_PATIENTS = 'GET_ALL_PATIENTS',
	APPLY_FILTERS = 'APPLY_FILTERS',
	ADD_PARAMS_TO_APPLY_FILTERS = 'ADD_PARAMS_TO_APPLY_FILTERS',
	REMOVE_FILTER_FROM_APPLY_FILTERS = 'REMOVE_FILTER_FROM_APPLY_FILTERS',
	CHANGE_PATIENT_DATA_REQUEST_FILTER = 'CHANGE_PATIENT_DATA_REQUEST_FILTER',
	SET_SELECTED_PATIENTS = 'SET_SELECTED_PATIENTS',
	SET_TOTAL_COUNT_PATIENTS = 'SET_TOTAL_COUNT_PATIENTS',
	SELECTED_ASSIGN_PATIENTS = 'SELECTED_ASSIGN_PATIENTS',
	IS_HOLD_SELECTED_PATIENTS = 'IS_HOLD_SELECTED_PATIENTS',
	RESET = 'SEARCH_PATIENTS_RESET',
}

export interface GetAllPatients {
	type: SEARCH_PATIENTS_ACTION_TYPES.GET_ALL_PATIENTS
	payload: Array<PatientDataTableModel>
	filteredPatientsCount: number | String
}

export interface SetTotalCountPatientsAction {
	type: SEARCH_PATIENTS_ACTION_TYPES.SET_TOTAL_COUNT_PATIENTS
	payload: number | String
}

export interface SearchApplyFilterAction {
	type: SEARCH_PATIENTS_ACTION_TYPES.APPLY_FILTERS
	payload: Array<PatientFilter>
}

export interface RemoveFilterApplyFilterAction {
	type: SEARCH_PATIENTS_ACTION_TYPES.REMOVE_FILTER_FROM_APPLY_FILTERS
	payload: PatientFilter
}

export interface ChangePatientDataRequestFilter {
	type: SEARCH_PATIENTS_ACTION_TYPES.CHANGE_PATIENT_DATA_REQUEST_FILTER
	key: string
	value: Array<PatientFilter> | String | null
}

export interface ChangePatient {
	type: SEARCH_PATIENTS_ACTION_TYPES.REMOVE_FILTER_FROM_APPLY_FILTERS
	payload: PatientFilter
}

export interface SelectPatientsArray {
	type: SEARCH_PATIENTS_ACTION_TYPES.SET_SELECTED_PATIENTS
	payload: Array<PatientDataTableModel>
}

export interface SelectedAssignPatients {
	type: SEARCH_PATIENTS_ACTION_TYPES.SELECTED_ASSIGN_PATIENTS
	payload: string[]
}

export interface IsHoldSelectedPatients {
	type: SEARCH_PATIENTS_ACTION_TYPES.IS_HOLD_SELECTED_PATIENTS
	payload: boolean
}

export interface SearchPatientsReset {
	type: SEARCH_PATIENTS_ACTION_TYPES.RESET
}

export type AppliersFilters = Array<PatientFilter>
export type PatientDataTableModelArrays = Array<PatientDataTableModel>

export interface SearchPatientsState {
	appliersFilters: AppliersFilters
	requestParamsFilter: PatientDataRequest
	selectedPatients: PatientDataTableModelArrays
	patientsDataArray: PatientDataTableModelArrays
	filteredPatientsCount: number | String
	totalPatientsCount: number | String | undefined
	selectedAssignPatients: string[]
	isHoldSelectedPatients: boolean
}

export type SearchPatientAction =
	| SearchApplyFilterAction
	| RemoveFilterApplyFilterAction
	| ChangePatientDataRequestFilter
	| SelectPatientsArray
	| GetAllPatients
	| SetTotalCountPatientsAction
	| SelectedAssignPatients
	| IsHoldSelectedPatients
	| SearchPatientsReset

export interface PatientDashboardState extends PatientDashboardData {
	notesForPatient: NotesForPatientState
	questionnaires: PatientQuestionnaireState
	pacientAccountData: PatientAccountDataState
	pacientAccountOrders: PatientAccountOrdersState
	checklist: ChecklistState
	tasks: PatientTasksState
	clinical: ClinicalState
	logBook: SystemEventsLogbookState
	reports: ReportsState
	timeline: TimeLineListState
	chart: ChartsState
}

export interface AssignPatientDataRequest {
	patientIds: Array<string>
	patientsAPIKey: Array<string>
	coachId?: string
	extCoachId: string
	coachName: string
	coachEmail: string
	clinicId: string
	logBook?: SystemEventsLogbookState
	reports?: ReportsState
	timeline?: TimeLineListState
}

export interface UnassignPatientDataRequest {
	patientApiKeys: Array<string>
	coachId: string
}
